import { createRandom } from '@/data/seed';
import {
  addDays,
  addMonths,
  addWeeks,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

// 이벤트 캘린더의 순수 로직 — 날짜 그리드 계산, 일정 배치(겹침), 입력 검증, 더미 시드.

export type CalendarView = 'month' | 'week';

export const EVENT_STATUSES = ['예정', '진행 중', '완료', '취소'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export interface CalEvent {
  id: string;
  title: string;
  /** yyyy-MM-dd — 하루 안에서 끝나는 일정만 다룬다 */
  date: string;
  /** 자정부터의 분 — 시각 문자열 대신 숫자로 두면 겹침·길이 계산이 단순하다 */
  startMin: number;
  endMin: number;
  status: EventStatus;
}

/** 주 보기에 그리는 시간대 [시작, 끝) — 08:00 ~ 20:00 */
export const HOUR_START = 8;
export const HOUR_END = 20;
export const MAX_TITLE_LENGTH = 40;

/** 월요일 시작 — 한국 업무 달력 관례 */
const WEEK_OPTIONS = { weekStartsOn: 1 } as const;

const DATE_KEY = 'yyyy-MM-dd';
export const toDateKey = (date: Date) => format(date, DATE_KEY);

/** URL의 보기 값 — 알 수 없는 값은 기본(월)로 돌린다. */
export const parseView = (raw: string): CalendarView => (raw === 'week' ? 'week' : 'month');

/**
 * URL의 기준 날짜 — 형식이 틀리거나 존재하지 않는 날짜(2026-02-31)는 fallback.
 * date-fns parse는 넘치는 날짜를 보정하지 않고 Invalid Date를 주므로 isValid로 걸러진다.
 */
export function parseAnchor(raw: string, fallback: Date): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return fallback;
  }
  const parsed = parse(raw, DATE_KEY, new Date());
  return isValid(parsed) ? parsed : fallback;
}

/** 월 보기 그리드 — 항상 6주(42칸)라 달마다 높이가 흔들리지 않는다. */
export function monthGrid(anchor: Date): Date[] {
  const first = startOfWeek(startOfMonth(anchor), WEEK_OPTIONS);
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

export function weekDays(anchor: Date): Date[] {
  const first = startOfWeek(anchor, WEEK_OPTIONS);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
}

/** 이전·다음 — 월 보기는 한 달, 주 보기는 한 주. 31일에서 달을 넘겨도 date-fns가 말일로 맞춘다. */
export function shiftAnchor(view: CalendarView, anchor: Date, direction: -1 | 1): Date {
  return view === 'month' ? addMonths(anchor, direction) : addWeeks(anchor, direction);
}

export const formatTime = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** "HH:mm" → 분. 형식이 틀리면 null. */
export function parseTime(raw: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
}

const byTime = (a: CalEvent, b: CalEvent) =>
  a.startMin - b.startMin || a.endMin - b.endMin || a.id.localeCompare(b.id);

/** 날짜별로 묶고 시작 시각 순으로 정렬한다. */
export function groupByDate(events: CalEvent[]): Map<string, CalEvent[]> {
  const map = new Map<string, CalEvent[]>();
  for (const event of [...events].sort(byTime)) {
    const list = map.get(event.date) ?? [];
    list.push(event);
    map.set(event.date, list);
  }
  return map;
}

/** 칸에 보일 만큼과 "+n개"로 접을 나머지. 딱 max개면 접지 않는다("+0개"를 만들지 않는다). */
export function splitVisible<T>(items: T[], max: number) {
  return { shown: items.slice(0, max), hidden: items.slice(max) };
}

export interface PlacedEvent {
  event: CalEvent;
  /** 보이는 시간대 시작(HOUR_START)부터의 분 */
  topMin: number;
  durationMin: number;
  /** 겹치는 묶음 안에서의 칸 번호와 칸 수 — 가로 위치·폭 계산용 */
  column: number;
  columns: number;
}

/**
 * 하루치 일정의 주 보기 배치. 서로(연쇄적으로) 겹치는 일정을 한 묶음으로 보고,
 * 묶음 안에서 먼저 비는 칸에 넣어 나란히 세운다. 묶음마다 칸 수가 달라서
 * 겹치지 않는 일정은 항상 전체 폭을 쓴다.
 * 보이는 시간대 밖으로 나간 부분은 잘라 내고, 완전히 밖이면 그리지 않는다.
 */
export function layoutDay(events: CalEvent[]): PlacedEvent[] {
  const lower = HOUR_START * 60;
  const upper = HOUR_END * 60;
  const visible = events
    .map((event) => ({
      event,
      start: Math.max(event.startMin, lower),
      end: Math.min(event.endMin, upper),
    }))
    .filter((item) => item.end > item.start)
    .sort((a, b) => byTime(a.event, b.event));

  const placed: PlacedEvent[] = [];
  let cluster: { event: CalEvent; start: number; end: number; column: number }[] = [];
  let columnEnds: number[] = [];
  let clusterEnd = -1;

  const flush = () => {
    for (const item of cluster) {
      placed.push({
        event: item.event,
        topMin: item.start - lower,
        durationMin: item.end - item.start,
        column: item.column,
        columns: columnEnds.length,
      });
    }
    cluster = [];
    columnEnds = [];
    clusterEnd = -1;
  };

  for (const item of visible) {
    // 지금 묶음이 끝난 뒤에 시작하면 새 묶음 — 맞닿기만 한 일정(끝 = 시작)은 겹치지 않는다.
    if (cluster.length > 0 && item.start >= clusterEnd) {
      flush();
    }
    let column = columnEnds.findIndex((end) => end <= item.start);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(item.end);
    } else {
      columnEnds[column] = item.end;
    }
    cluster.push({ ...item, column });
    clusterEnd = Math.max(clusterEnd, item.end);
  }
  flush();
  return placed;
}

export interface EventDraft {
  title: string;
  date: string;
  start: string;
  end: string;
}
export type DraftErrors = Partial<Record<keyof EventDraft, string>>;

/** 필드별 오류 — 무엇이 틀렸고 어떻게 고치는지까지 말한다. 오류가 없으면 빈 객체. */
export function validateDraft(draft: EventDraft): DraftErrors {
  const errors: DraftErrors = {};
  const title = draft.title.trim();
  if (!title) {
    errors.title = '제목을 입력하세요.';
  } else if (title.length > MAX_TITLE_LENGTH) {
    errors.title = `제목은 ${String(MAX_TITLE_LENGTH)}자 이하로 입력하세요.`;
  }
  if (!draft.date) {
    errors.date = '날짜를 선택하세요.';
  }
  const start = parseTime(draft.start);
  const end = parseTime(draft.end);
  const rangeLabel = `${String(HOUR_START).padStart(2, '0')}:00 ~ ${String(HOUR_END)}:00`;
  if (start === null) {
    errors.start = '시작 시각을 입력하세요.';
  } else if (start < HOUR_START * 60 || start >= HOUR_END * 60) {
    errors.start = `시작 시각은 ${rangeLabel} 사이여야 합니다.`;
  }
  if (end === null) {
    errors.end = '종료 시각을 입력하세요.';
  } else if (end > HOUR_END * 60 || end <= HOUR_START * 60) {
    errors.end = `종료 시각은 ${rangeLabel} 사이여야 합니다.`;
  } else if (start !== null && end <= start) {
    errors.end = '종료 시각은 시작 시각보다 늦어야 합니다.';
  }
  return errors;
}

const TITLES = [
  '주간 점검',
  '정기 회의',
  '자료 검토',
  '현장 확인',
  '일정 조율',
  '교육 진행',
  '결과 보고',
  '항목 정리',
];

/** 더미라 실제 현재 시각 대신 정오를 "지금"으로 본다 — 열 때마다 같은 모양이 나오게 */
const SEED_NOW_MIN = 12 * 60;

/** 지난 일정은 완료, 앞으로는 예정. 오늘은 정오 기준으로 완료·진행 중·예정이 모두 나온다. 취소는 어느 날이든 가끔. */
function statusFor(offset: number, startMin: number, endMin: number, cancel: boolean): EventStatus {
  if (cancel) {
    return '취소';
  }
  if (offset < 0 || (offset === 0 && endMin <= SEED_NOW_MIN)) {
    return '완료';
  }
  return offset === 0 && startMin <= SEED_NOW_MIN ? '진행 중' : '예정';
}

/**
 * 오늘 기준 앞뒤 3주의 더미 일정. 시드 고정이라 열 때마다 같은 모양이고,
 * 오늘·모레는 "+n개"와 겹침 배치를 바로 볼 수 있게 일정을 많이 넣는다.
 */
export function createSeedEvents(today: Date): CalEvent[] {
  const random = createRandom(2026);
  const events: CalEvent[] = [];
  for (let offset = -21; offset <= 21; offset += 1) {
    const day = addDays(today, offset);
    const weekend = day.getDay() === 0 || day.getDay() === 6;
    const crowded = offset === 0 || offset === 2;
    const count = crowded ? 5 : weekend ? (random.chance(0.2) ? 1 : 0) : random.int(0, 3);
    for (let i = 0; i < count; i += 1) {
      const startMin = random.int(HOUR_START, HOUR_END - 2) * 60 + random.pick([0, 30]);
      const endMin = Math.min(startMin + random.pick([30, 60, 90, 120]), HOUR_END * 60);
      events.push({
        id: `E-${String(events.length + 1)}`,
        title: random.pick(TITLES),
        date: toDateKey(day),
        startMin,
        endMin,
        status: statusFor(offset, startMin, endMin, random.chance(0.1)),
      });
    }
  }
  return events;
}
